import { ENDPOINTS } from "../config/apiConfig";

/**
 * Format raw profile object into standard card data format
 * @param {object} data 
 * @param {string} fallbackId 
 * @returns {object}
 */
export function formatProfileData(data, fallbackId = "") {
  if (!data) return null;

  const logoUrl = data.logo_data
    ? `data:${data.logo_mime_type || "image/png"};base64,${data.logo_data}`
    : null;

  const heading = data.heading ? String(data.heading).trim() : "";
  const description = data.description ? String(data.description).trim() : "";

  return {
    id: data.id || data.tenant_id || fallbackId,
    tenantId: data.tenant_id || fallbackId,
    heading,
    description,
    logoUrl,
    raw: data,
  };
}

/**
 * Fetch all public tenant profiles automatically
 * GET /public/tenants/profiles (or fallback GET /public/tenants)
 * @returns {Promise<Array<{id: string, heading: string, description: string, logoUrl: string | null}>>}
 */
export async function fetchAllPublicTenantProfiles() {
  const endpointsToTry = [
    ENDPOINTS.ALL_PUBLIC_TENANT_PROFILES,
    ENDPOINTS.PUBLIC_TENANTS,
  ];

  for (const endpoint of endpointsToTry) {
    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) continue;

      const result = await response.json();
      const rawList = result?.data || result;

      if (Array.isArray(rawList)) {
        return rawList
          .map((item, index) => formatProfileData(item, `tenant-${index}`))
          .filter(Boolean);
      }
    } catch (error) {
      console.warn(`Failed to fetch from ${endpoint}:`, error);
    }
  }

  return [];
}

/**
 * Fetch public tenant profile by tenant ID
 * @param {string} tenantId 
 * @returns {Promise<{id: string, heading: string, description: string, logoUrl: string | null, raw: object}>}
 */
export async function fetchPublicTenantProfile(tenantId) {
  try {
    const url = ENDPOINTS.PUBLIC_TENANT_PROFILE(tenantId);
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tenant profile (${response.status} ${response.statusText})`);
    }

    const result = await response.json();
    const data = result?.data || result;

    if (!data) {
      throw new Error("Invalid response format from tenant profile API");
    }

    return formatProfileData(data, tenantId);
  } catch (error) {
    console.error(`Error fetching tenant profile for ${tenantId}:`, error);
    throw error;
  }
}

/**
 * Fetch multiple tenant profiles concurrently by IDs
 * @param {string[]} tenantIds 
 * @returns {Promise<Array<{id: string, heading: string, description: string, logoUrl: string | null}>>}
 */
export async function fetchMultipleTenantProfiles(tenantIds = []) {
  if (!Array.isArray(tenantIds) || tenantIds.length === 0) {
    return [];
  }

  const results = await Promise.allSettled(
    tenantIds.map((id) => fetchPublicTenantProfile(id))
  );

  return results
    .filter((res) => res.status === "fulfilled" && res.value)
    .map((res) => res.value);
}
